import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLinks } from '../../hooks/useLinks';
import { useDomains } from '../../hooks/useDomains';
import ModernLinkCard from './ModernLinkCard';
import ImprovedCreateLinkModal from './ImprovedCreateLinkModal';
import ModernQRModal from './ModernQRModal';
import { getDefaultDomain } from '../../utils/domainUtils';

const ModernLinkList = () => {
  const navigate = useNavigate();
  const {
    links,
    loading,
    error,
    pagination,
    deleteLink,
    loadLinks,
    loadMoreLinks,
    getUserTags,
    clearError
  } = useLinks();

  const { domains } = useDomains();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedLink, setSelectedLink] = useState(null);
  const [selectedLinks, setSelectedLinks] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDomain, setFilterDomain] = useState('all');
  const [filterTags, setFilterTags] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('created');
  const [sortOrder, setSortOrder] = useState('desc');

  const userTags = getUserTags();
  const verifiedDomains = domains.filter(d => d.isVerified);
  const defaultDomain = getDefaultDomain(domains);

  // Load links with current filters
  useEffect(() => {
    const options = {
      search: searchQuery,
      domain: filterDomain,
      tags: filterTags,
      status: filterStatus,
      sortBy,
      sortOrder,
      page: 1,
      limit: 20
    };
    loadLinks(options);
  }, [searchQuery, filterDomain, filterTags, filterStatus, sortBy, sortOrder, loadLinks]);

  const handleCreateSuccess = (newLink) => {
    setShowCreateModal(false);
    // Force refresh the links list
    loadLinks();
  };

  const handleEditClick = (link) => {
    navigate(`/admin/links/${link.id}/edit`);
  };

  const handleShareClick = (link) => {
    setSelectedLink(link);
    setShowQRModal(true);
  };

  const handleQRClose = () => {
    setShowQRModal(false);
    setSelectedLink(null);
  };

  const handleDeleteClick = async (linkId) => {
    if (window.confirm('Are you sure you want to delete this link? This action cannot be undone.')) {
      try {
        await deleteLink(linkId);
        if (selectedLinks.has(linkId)) {
          const newSelected = new Set(selectedLinks);
          newSelected.delete(linkId);
          setSelectedLinks(newSelected);
        }
      } catch (error) {
        alert('Failed to delete link: ' + error.message);
      }
    }
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setFilterDomain('all');
    setFilterTags([]);
    setFilterStatus('all');
    setSortBy('created');
    setSortOrder('desc');
  };

  const hasActiveFilters = searchQuery || filterDomain !== 'all' || filterTags.length > 0 || filterStatus !== 'all';

  return (
    <div className="modern-link-list">
      {/* Modern Header */}
      <div className="page-header">
        <div className="header-content">
          <div className="title-section">
            <h1>My Links</h1>
            <p>Create, manage, and track your short links</p>
          </div>
          <button
            className="create-btn"
            onClick={() => setShowCreateModal(true)}
            disabled={loading}
          >
            <span className="btn-icon">✨</span>
            Create Link
          </button>
        </div>

        {/* Quick Stats */}
        <div className="quick-stats">
          <div className="stat-card">
            <div className="stat-value">{(links || []).length}</div>
            <div className="stat-label">Total Links</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {(links || []).reduce((sum, link) => sum + (link.clickCount || 0), 0)}
            </div>
            <div className="stat-label">Total Clicks</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {(links || []).filter(link => link.isActive).length}
            </div>
            <div className="stat-label">Active</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{verifiedDomains.length}</div>
            <div className="stat-label">Domains</div>
          </div>
        </div>
      </div>

      {/* Modern Filters */}
      <div className="filters-section">
        <div className="search-container">
          <div className="search-input-wrapper">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search links, URLs, or titles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button
                className="clear-search"
                onClick={() => setSearchQuery('')}
              >
                ×
              </button>
            )}
          </div>
        </div>

        <div className="filter-controls">
          <select
            value={filterDomain}
            onChange={(e) => setFilterDomain(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Domains</option>
            <option value="">Default ({defaultDomain})</option>
            {verifiedDomains.map(domain => (
              <option key={domain.id} value={domain.id}>
                {domain.domain}
              </option>
            ))}
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="inactive">Inactive</option>
          </select>

          <select
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [sort, order] = e.target.value.split('-');
              setSortBy(sort);
              setSortOrder(order);
            }}
            className="filter-select"
          >
            <option value="created-desc">Newest First</option>
            <option value="created-asc">Oldest First</option>
            <option value="clicks-desc">Most Clicks</option>
            <option value="clicks-asc">Least Clicks</option>
            <option value="title-asc">Title (A-Z)</option>
            <option value="title-desc">Title (Z-A)</option>
          </select>

          {hasActiveFilters && (
            <button
              className="clear-filters-btn"
              onClick={clearAllFilters}
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="error-state">
          <div className="error-content">
            <span className="error-icon">⚠️</span>
            <div className="error-text">
              <h3>Something went wrong</h3>
              <p>{error}</p>
            </div>
            <button
              className="retry-btn"
              onClick={() => {
                clearError();
                loadLinks();
              }}
            >
              Try Again
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (links || []).length === 0 && (
        <div className="loading-state">
          <div className="loading-content">
            <div className="modern-spinner"></div>
            <h3>Loading your links...</h3>
            <p>Just a moment while we fetch your data</p>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && (links || []).length === 0 && !error && (
        <div className="empty-state">
          <div className="empty-content">
            <div className="empty-icon">🚀</div>
            <h3>Ready to create your first link?</h3>
            <p>Transform long URLs into short, trackable links in seconds</p>
            <button
              className="create-first-btn"
              onClick={() => setShowCreateModal(true)}
            >
              <span className="btn-icon">✨</span>
              Create Your First Link
            </button>
          </div>
        </div>
      )}

      {/* Modern Links Grid */}
      {(links || []).length > 0 && (
        <div className="links-container">
          <div className="links-list">
            {(links || []).map((link) => (
              <ModernLinkCard
                key={link.id}
                link={link}
                onEdit={handleEditClick}
                onDelete={handleDeleteClick}
                onShare={handleShareClick}
                onToggleSelect={() => {}} // Simplified for now
                isSelected={false}
              />
            ))}
          </div>

          {/* Load More */}
          {pagination?.hasMore && (
            <div className="load-more-section">
              <button
                className="load-more-btn"
                onClick={() => loadMoreLinks()}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="btn-spinner"></div>
                    Loading...
                  </>
                ) : (
                  <>
                    Load More
                    <span className="remaining-count">
                      ({pagination.total - (links || []).length} remaining)
                    </span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      <ImprovedCreateLinkModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />

      {showQRModal && selectedLink && (
        <ModernQRModal
          link={selectedLink}
          isOpen={showQRModal}
          onClose={handleQRClose}
        />
      )}

      <style jsx>{`
        .modern-link-list {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .page-header {
          margin-bottom: 32px;
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 24px;
        }

        .title-section h1 {
          margin: 0 0 8px 0;
          font-size: 32px;
          color: #1a1a1a;
          font-weight: 700;
        }

        .title-section p {
          margin: 0;
          color: #6b7280;
          font-size: 16px;
        }

        .create-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          padding: 14px 24px;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
        }

        .create-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
        }

        .create-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }

        .btn-icon {
          font-size: 18px;
        }

        .quick-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 16px;
          margin-bottom: 24px;
        }

        .stat-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 20px;
          text-align: center;
          transition: all 0.2s ease;
        }

        .stat-card:hover {
          border-color: #d1d5db;
          box-shadow: 0 4px 8px rgba(0,0,0,0.05);
        }

        .stat-value {
          font-size: 28px;
          font-weight: 700;
          color: #1a1a1a;
          margin-bottom: 4px;
        }

        .stat-label {
          font-size: 12px;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-weight: 500;
        }

        .filters-section {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          padding: 20px;
          margin-bottom: 24px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
        }

        .search-container {
          margin-bottom: 16px;
        }

        .search-input-wrapper {
          position: relative;
          max-width: 400px;
        }

        .search-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #6b7280;
          font-size: 16px;
        }

        .search-input {
          width: 100%;
          padding: 14px 14px 14px 44px;
          border: 1px solid #d1d5db;
          border-radius: 12px;
          font-size: 16px;
          background: #f9fafb;
          transition: all 0.2s ease;
        }

        .search-input:focus {
          outline: none;
          border-color: #667eea;
          background: white;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .clear-search {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          background: #6b7280;
          color: white;
          border: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          cursor: pointer;
          font-size: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .filter-controls {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          align-items: center;
        }

        .filter-select {
          padding: 10px 14px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          background: white;
          min-width: 140px;
          cursor: pointer;
        }

        .filter-select:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
        }

        .clear-filters-btn {
          background: #f3f4f6;
          color: #374151;
          border: 1px solid #d1d5db;
          padding: 10px 16px;
          border-radius: 8px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .clear-filters-btn:hover {
          background: #e5e7eb;
        }

        .error-state,
        .loading-state,
        .empty-state {
          text-align: center;
          padding: 80px 20px;
        }

        .error-content,
        .loading-content,
        .empty-content {
          max-width: 400px;
          margin: 0 auto;
        }

        .error-icon,
        .empty-icon {
          font-size: 64px;
          margin-bottom: 20px;
        }

        .modern-spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #f3f4f6;
          border-top: 4px solid #667eea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }

        .error-content h3,
        .loading-content h3,
        .empty-content h3 {
          margin: 0 0 12px 0;
          font-size: 24px;
          color: #1a1a1a;
          font-weight: 600;
        }

        .error-content p,
        .loading-content p,
        .empty-content p {
          margin: 0 0 24px 0;
          color: #6b7280;
          font-size: 16px;
          line-height: 1.5;
        }

        .retry-btn,
        .create-first-btn {
          background: #667eea;
          color: white;
          border: none;
          padding: 14px 24px;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .retry-btn:hover,
        .create-first-btn:hover {
          background: #5a6fd8;
          transform: translateY(-2px);
        }

        .links-container {
          margin-top: 24px;
        }

        .links-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-bottom: 32px;
        }

        .load-more-section {
          text-align: center;
          margin: 32px 0;
        }

        .load-more-btn {
          background: white;
          border: 2px solid #e5e7eb;
          color: #374151;
          padding: 12px 24px;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .load-more-btn:hover:not(:disabled) {
          border-color: #667eea;
          color: #667eea;
          transform: translateY(-1px);
        }

        .load-more-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .remaining-count {
          color: #6b7280;
          font-weight: normal;
        }

        .btn-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid #f3f4f6;
          border-top: 2px solid currentColor;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
          .header-content {
            flex-direction: column;
            gap: 16px;
            text-align: center;
          }

          .quick-stats {
            grid-template-columns: repeat(2, 1fr);
          }

          .filter-controls {
            flex-direction: column;
            align-items: stretch;
          }

          .links-list {
            gap: 12px;
          }

          .search-input-wrapper {
            max-width: none;
          }
        }

        @media (max-width: 480px) {
          .modern-link-list {
            padding: 0 16px;
          }

          .quick-stats {
            grid-template-columns: 1fr;
          }

          .title-section h1 {
            font-size: 28px;
          }
        }
      `}</style>
    </div>
  );
};

export default ModernLinkList;